import { createContext, CSSProperties, PropsWithChildren, useContext } from 'react';
import {
  Palette,
  PALETTE_VARIABLES,
} from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/config/palette-config';
import {
  FontPair,
  TYPOGRAPHY_VARIABLES,
} from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/config/font-config';
import useThemeFonts from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/hooks/use-theme-fonts';
import { WEDDING_WEBSITE_ATOMIC_COLORS } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/config/wedding-website-atomic-colors';

export type LayoutVariant = 'classic' | 'split';

const LAYOUT_VARIABLES: Record<LayoutVariant, Record<string, string>> = {
  classic: {
    '--divider-width-lg': '440px',
    '--section-padding-lg': '150px',
  },
  split: {
    '--divider-width-lg': '120px',
    '--section-padding-lg': '40px',
  },
};

interface Props extends PropsWithChildren {
  palette: Palette;
  fontPair: FontPair;
  layoutVariant?: LayoutVariant;
  preloadAllFonts?: boolean;
  className?: string;
}

const ThemeContext = createContext<{
  palette: Palette;
  fontPair: FontPair;
  layoutVariant: LayoutVariant;
}>({
  palette: Palette.Bridebook,
  fontPair: FontPair.Bridebook,
  layoutVariant: 'classic',
});

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({
  palette,
  fontPair,
  layoutVariant = 'classic',
  children,
  preloadAllFonts = false,
  className,
}: Props) => {
  // Load fonts for the current theme (or all themes if preloadAllFonts is true)
  useThemeFonts({ fontPair, preloadAll: preloadAllFonts });

  const allStyles = {
    ...WEDDING_WEBSITE_ATOMIC_COLORS,
    ...PALETTE_VARIABLES[palette],
    ...TYPOGRAPHY_VARIABLES[fontPair],
    ...LAYOUT_VARIABLES[layoutVariant],
  };

  return (
    <ThemeContext.Provider value={{ palette, fontPair, layoutVariant }}>
      <div style={allStyles as CSSProperties} className={className}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
};
